import baseCatalog from "../data/catalog.json";
import menuData from "../data/menu.json";

const delay = (ms = 50) => new Promise((resolve) => setTimeout(resolve, ms));

const normalizeCatalogKey = (value = "") =>
  value.toLowerCase().replace(/[^a-z0-9]+/g, "");

export const getMergedCatalog = () => {
  const catalog = JSON.parse(JSON.stringify(baseCatalog));
  
  const overrides = JSON.parse(localStorage.getItem("catalog_products_override") || "{}");
  const deleted = JSON.parse(localStorage.getItem("catalog_products_deleted") || "[]");
  const deletedSet = new Set(deleted.map(p => normalizeCatalogKey(p)));
  
  catalog.forEach((catObj) => {
    catObj.products = catObj.products.filter(p => !deletedSet.has(normalizeCatalogKey(p.partNumber)));
    catObj.sections.forEach((sec) => {
      sec.partNumbers = (sec.partNumbers || []).filter(pn => !deletedSet.has(normalizeCatalogKey(pn)));
    });
  });
  
  Object.keys(overrides).forEach((partNumber) => {
    const override = overrides[partNumber];
    const normPartNumber = normalizeCatalogKey(partNumber);
    
    if (deletedSet.has(normPartNumber)) return;
    
    const targetCatNorm = normalizeCatalogKey(override.category);
    
    let catObj = catalog.find(c => normalizeCatalogKey(c.category) === targetCatNorm);
    if (!catObj) {
      catObj = {
        category: override.category,
        sections: [],
        products: []
      };
      catalog.push(catObj);
    }
    
    let existingProdIndex = catObj.products.findIndex(p => normalizeCatalogKey(p.partNumber) === normPartNumber);
    const mappedProduct = {
      partNumber: override.partNumber,
      description: override.description || "",
      imageUrl: override.imageUrl || "",
      datasheetUrl: override.datasheetUrl || "",
      cableSpecs: override.cableSpecs || [],
      electricalSpecs: override.electricalSpecs || [],
      mechanicalSpecs: override.mechanicalSpecs || []
    };
    
    if (existingProdIndex >= 0) {
      catObj.products[existingProdIndex] = mappedProduct;
    } else {
      catObj.products.push(mappedProduct);
    }
    
    const sectionHeading = override.sectionHeading || "General";
    const normSectionHeading = normalizeCatalogKey(sectionHeading);
    
    let sectionObj = catObj.sections.find(s => normalizeCatalogKey(s.heading) === normSectionHeading);
    if (!sectionObj) {
      sectionObj = {
        heading: sectionHeading,
        partNumbers: []
      };
      catObj.sections.push(sectionObj);
    }
    
    if (!sectionObj.partNumbers.some(pn => normalizeCatalogKey(pn) === normPartNumber)) {
      sectionObj.partNumbers.push(override.partNumber);
    }
  });
  
  return catalog;
};

export const fetchCatalogFamilies = async () => {
  await delay();
  return menuData || [];
};

export const fetchCategoryDetails = async (selection) => {
  await delay();
  
  const category = typeof selection === "string" ? selection : selection?.category || "";
  const label = typeof selection === "string" ? selection : selection?.label || "";
  const section = typeof selection === "string" ? "" : selection?.section || "";
  
  const normalizedCategory = normalizeCatalogKey(category);
  
  if (!normalizedCategory) {
    throw new Error("A category is required.");
  }
  
  const catalog = getMergedCatalog();
  const categoryObj = catalog.find(c => normalizeCatalogKey(c.category) === normalizedCategory);
  
  if (!categoryObj) {
    throw new Error("Category not found.");
  }
  
  if (!section) {
    return {
      category: categoryObj.category,
      label: label || categoryObj.category,
      section: null,
      samplePartNo: categoryObj.samplePartNo || (categoryObj.products?.[0]?.partNumber || ""),
      sections: categoryObj.sections,
      products: categoryObj.products
    };
  }
  
  const requestedSectionKey = normalizeCatalogKey(section);
  const filteredSections = categoryObj.sections.filter((s) => {
    const headingKey = normalizeCatalogKey(s.heading);
    return (
      headingKey === requestedSectionKey ||
      headingKey.startsWith(requestedSectionKey) ||
      requestedSectionKey.startsWith(headingKey)
    );
  });
  
  if (filteredSections.length === 0) {
    return {
      category: categoryObj.category,
      label: label || categoryObj.category,
      section: section,
      samplePartNo: categoryObj.samplePartNo || (categoryObj.products?.[0]?.partNumber || ""),
      sections: categoryObj.sections,
      products: categoryObj.products
    };
  }
  
  const allowedPartNumbers = new Set(
    filteredSections.flatMap((s) => s.partNumbers || [])
  );
  
  return {
    category: categoryObj.category,
    label: label || categoryObj.category,
    section: section,
    samplePartNo: categoryObj.samplePartNo || (categoryObj.products?.[0]?.partNumber || ""),
    sections: filteredSections,
    products: categoryObj.products.filter(p => allowedPartNumbers.has(p.partNumber))
  };
};
